import { kanalSuz, subeSuz } from './subeSuzgeci';
import { kisaTutar, kisaTutarIsaretsiz } from './bicim';

/** API sayilari metin olarak da dondurebilir. */
export type Sayisal = number | string | null | undefined;

export function sayi(x: unknown): number {
  const n = Number(x ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** 990040 Satış Kanalı Özeti — fis tabani, istemcide sube/kanal suzulur. */
export interface KanalSatiri {
  tarih: string;
  lokasyon: string;
  kanal_grup: string;
  kanal: string;
  fis_sayisi: Sayisal;
  net_ciro: Sayisal;
}

/** 990051 Satış Analiz · Kategori. `urun_disi` satirlarinda kar/pay/a_urun/a_ciro_pay/onceki_kar null. */
export interface SaKategoriSatiri {
  tur: string;
  ad: string;
  ciro: Sayisal;
  kar: Sayisal;
  pay: Sayisal;
  a_urun: Sayisal;
  a_ciro_pay: Sayisal;
  onceki_ciro: Sayisal;
  onceki_kar: Sayisal;
}

/** 990052 Satış Analiz · Günlük Kâr. */
export interface GunlukKarSatiri {
  gun: string;
  urun_ciro: Sayisal;
  kar: Sayisal;
}

export function yuzdeDegisim(bu: number, onceki: number | null): number | null {
  if (onceki === null || onceki === 0) return null;
  return ((bu - onceki) / Math.abs(onceki)) * 100;
}

export interface FisOzeti {
  ciro: number;
  fis: number;
  gunluk: Array<{ tarih: string; ciro: number; fis: number }>;
}

export function fisOzeti(rows: KanalSatiri[], sube: string | null, kanal: string | null): FisOzeti {
  const gunler = new Map<string, { ciro: number; fis: number }>();
  let ciro = 0;
  let fis = 0;
  for (const r of kanalSuz(subeSuz(rows, sube), kanal)) {
    const tarih = String(r.tarih ?? '').slice(0, 10);
    const c = sayi(r.net_ciro);
    const n = sayi(r.fis_sayisi);
    ciro += c;
    fis += n;
    const g = gunler.get(tarih) ?? { ciro: 0, fis: 0 };
    g.ciro += c;
    g.fis += n;
    gunler.set(tarih, g);
  }
  const gunluk = Array.from(gunler.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([tarih, g]) => ({ tarih, ...g }));
  return { ciro, fis, gunluk };
}

/**
 * Sepet ortalamasi YALNIZ Magaza kanalindan (K2 kapanis sarti): yemek karti operatorleri ve kurumsal
 * musteriler ayda tek, yuz binlerce liralik fatura keser; katilirsa ortalama %15 sisiyordu (olculdu).
 */
export function magazaSepeti(rows: KanalSatiri[], sube: string | null): { sepet: number | null; gunluk: number[] } {
  const m = fisOzeti(rows, sube, 'Mağaza');
  return {
    sepet: m.fis > 0 ? m.ciro / m.fis : null,
    gunluk: m.gunluk.filter((g) => g.fis > 0).map((g) => g.ciro / g.fis),
  };
}

export interface KarOzeti {
  kar: number;
  urunCiro: number;
  marj: number | null;
  oncekiKar: number | null;
  oncekiMarj: number | null;
}

/**
 * Brut kar karti. 🔴 Marj paydasi URUN cirosu (990051 Σ kategori ciro), fis cirosu (990040) DEGIL: kar
 * yalniz urun satirlarindan hesaplaniyor; fis cirosuna bolmek yemek karti/rehin gibi urun disi tutarlari
 * paydaya katip marji dusuk gosterir (Agustos: %26,46 yerine %24,31). Regresyon testi kilitler.
 * K10 (final-fix F-1): 990051 yalniz ONCEKI pencerede satan kategoriyi de dondurur (`ciro` NULL). Bu satir
 * cari toplamlara GIRMEZ, onceki toplamlara GIRER — onceki kar 990040'in tam onceki penceresiyle ayni kitle.
 */
export function karOzeti(rows: SaKategoriSatiri[]): KarOzeti | null {
  const kat = rows.filter((r) => r.tur === 'kategori');
  if (kat.length === 0) return null;
  let kar = 0;
  let ciro = 0;
  let oncekiKar = 0;
  let oncekiCiro = 0;
  let oncekiVar = false;
  for (const r of kat) {
    if (r.ciro !== null && r.ciro !== undefined) {
      kar += sayi(r.kar);
      ciro += sayi(r.ciro);
    }
    if (r.onceki_ciro !== null && r.onceki_ciro !== undefined) {
      oncekiVar = true;
      oncekiKar += sayi(r.onceki_kar);
      oncekiCiro += sayi(r.onceki_ciro);
    }
  }
  return {
    kar,
    urunCiro: ciro,
    marj: ciro !== 0 ? (kar / ciro) * 100 : null,
    oncekiKar: oncekiVar ? oncekiKar : null,
    oncekiMarj: oncekiVar && oncekiCiro !== 0 ? (oncekiKar / oncekiCiro) * 100 : null,
  };
}

/** Urun disi kalemlerin okunur adlari (spec §2.1: "Tüketime Hazır Gıda" = yemek karti satisi). */
export const KOPRU_KALEM_ADLARI: Record<string, string> = {
  'TÜKETİME HAZIR GIDA': 'Yemek kartı',
  'REHİN': 'Rehin',
};

/** Notta adiyla yazilan kalem sayisi; kalanlar "diger"de toplanir. */
const KOPRU_ADLI_KALEM_SAYISI = 2;

export interface KopruNotu {
  urunCiro: number;
  urunDisi: number;
  kalemler: Array<{ ad: string; ciro: number }>;
  diger: number | null;
}

/** K2 kopru notu: fis tabani (buyuk ciro) ile urun tabani arasindaki fark. Urun disi toplam 0 ise null. */
export function kopruNotu(rows: SaKategoriSatiri[]): KopruNotu | null {
  const urunCiro = rows.filter((r) => r.tur === 'kategori').reduce((t, r) => t + sayi(r.ciro), 0);
  const disi = rows
    .filter((r) => r.tur === 'urun_disi')
    .map((r) => ({ ad: String(r.ad), ciro: sayi(r.ciro) }))
    .sort((a, b) => Math.abs(b.ciro) - Math.abs(a.ciro));
  const urunDisi = disi.reduce((t, r) => t + r.ciro, 0);
  if (urunDisi === 0) return null;
  const adli = disi.slice(0, KOPRU_ADLI_KALEM_SAYISI);
  const kalan = disi.slice(KOPRU_ADLI_KALEM_SAYISI);
  return {
    urunCiro,
    urunDisi,
    kalemler: adli.map((k) => ({ ad: KOPRU_KALEM_ADLARI[k.ad] ?? k.ad, ciro: k.ciro })),
    diger: kalan.length > 0 ? kalan.reduce((t, r) => t + r.ciro, 0) : null,
  };
}

/**
 * CONTROLLER RULING C-4: ₺ isareti YALNIZ iki ana tutarda ("Ürün satışı", "ürün dışı"); alt kalemler
 * (adli kalemler + "diğer") isaretsiz kisa tutarla yazilir (spec §3 ornegi: "Yemek kartı 1,00 Mn").
 */
export function kopruNotuMetni(k: KopruNotu): string {
  const kalemler = k.kalemler.map((x) => `${x.ad} ${kisaTutarIsaretsiz(x.ciro)}`);
  if (k.diger !== null) kalemler.push(`diğer ${kisaTutarIsaretsiz(k.diger)}`);
  return `Ürün satışı ${kisaTutar(k.urunCiro)} + ürün dışı ${kisaTutar(k.urunDisi)}: ${kalemler.join(' · ')}`;
}
